import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import {
  ShareIcon,
  UserIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { useAgentSharing } from '@/hooks/useAgentSharing'
import type { SharedAgentResponse } from '@/services/api/models/SharedAgentResponse'
import { clsx } from 'clsx'

interface SharedAgentsPanelProps {
  refreshTrigger?: number
  onAgentSelect?: (agent: SharedAgentResponse) => void
  onAgentLeave?: (newCount?: number) => void
  onSharedAgentsUpdate?: (sharedAgents: SharedAgentResponse[]) => void
  initialSharedAgents?: SharedAgentResponse[]
}

// Memoized shared agent item component to prevent unnecessary re-renders
interface SharedAgentItemProps {
  agent: SharedAgentResponse
  onAgentSelect?: (agent: SharedAgentResponse) => void
  onLeaveClick: (agent: SharedAgentResponse) => void
  formatDate: (dateString: string) => string
  getPermissionBadge: (permission: string) => React.ReactNode
}

const SharedAgentItem = React.memo<SharedAgentItemProps>(({ 
  agent, 
  onAgentSelect, 
  onLeaveClick,
  formatDate,
  getPermissionBadge
}) => {
  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <div className="flex items-start justify-between">
        <div 
          className={clsx(
            'flex-1 min-w-0',
            onAgentSelect && 'cursor-pointer'
          )}
          onClick={() => onAgentSelect?.(agent)}
        >
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <ShareIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {agent.name}
              </h4>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <UserIcon className="w-4 h-4" />
                <span>Owner: {agent.owner_name}</span>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1">
              {getPermissionBadge(agent.permission || 'read')}
              {agent.shared_by_name && agent.shared_by_name !== agent.owner_name && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  via {agent.shared_by_name}
                </div>
              )}
            </div>
          </div>

          {agent.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
              {agent.description}
            </p>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span>
                Shared {agent.created_at && formatDate(agent.created_at)}
              </span>
            </div>
            
            {onAgentSelect && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-blue-600 dark:text-blue-400">
                  Click to view →
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Leave Button */}
        <div className="flex items-center ml-4">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onLeaveClick(agent)
            }}
            className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
            title="Leave agent"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Only re-render if agent data changes
  return JSON.stringify(prevProps.agent) === JSON.stringify(nextProps.agent)
})

SharedAgentItem.displayName = 'SharedAgentItem'

export function SharedAgentsPanel({ refreshTrigger, onAgentSelect, onAgentLeave, onSharedAgentsUpdate, initialSharedAgents }: SharedAgentsPanelProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  const [agentToLeave, setAgentToLeave] = useState<SharedAgentResponse | null>(null)
  const [leaveLoading, setLeaveLoading] = useState(false)
  const [localSharedAgents, setLocalSharedAgents] = useState<SharedAgentResponse[]>(initialSharedAgents || [])
  const [loading, setLoading] = useState(!initialSharedAgents)
  const [error, setError] = useState<string | null>(null)
  
  const { 
    fetchSharedAgents, 
    leaveSharedAgent 
  } = useAgentSharing()

  // Track if we've done initial fetch
  const hasFetchedRef = useRef(false)
  const lastRefreshTriggerRef = useRef(refreshTrigger || 0)
  
  // Function to fetch shared agents data and update local state
  const fetchSharedAgentsData = useCallback(async (isRefresh = false) => {
    try {
      // Only show loading state for initial load, not for refresh
      if (!isRefresh) {
        setLoading(true)
      }
      setError(null)
      
      // Fetch fresh data from the API
      const response = await fetchSharedAgents()
      
      // Smart merge: only update agents that have actually changed
      if (isRefresh) {
        setLocalSharedAgents(prevAgents => {
          // Create a map of new agents for quick lookup
          const newAgentsMap = new Map(response.map(agent => [agent.id, agent]))
          
          // Update existing agents or add new ones
          const updatedAgents = prevAgents.map(prevAgent => {
            const newAgent = newAgentsMap.get(prevAgent.id)
            if (newAgent) {
              // Only update if there are actual changes
              const hasChanges = JSON.stringify(prevAgent) !== JSON.stringify(newAgent)
              newAgentsMap.delete(prevAgent.id) // Remove from map as it's been processed
              return hasChanges ? newAgent : prevAgent
            }
            return prevAgent
          })
          
          // Add any new agents that weren't in the previous list
          const newAgents = Array.from(newAgentsMap.values())
          
          // Filter out agents that are no longer in the response
          const agentIds = new Set(response.map(a => a.id))
          const finalAgents = updatedAgents.filter(a => agentIds.has(a.id))
          
          const newState = [...finalAgents, ...newAgents]
          onSharedAgentsUpdate?.(newState)
          return newState
        })
      } else {
        setLocalSharedAgents(response)
        onSharedAgentsUpdate?.(response)
      }
    } catch (err: unknown) {
      console.error('Failed to fetch shared agents:', err)
      if (!isRefresh) {
        setError(err instanceof Error ? err.message : 'Failed to load shared agents')
      }
    } finally {
      if (!isRefresh) {
        setLoading(false)
      }
    }
  }, [fetchSharedAgents, onSharedAgentsUpdate])
  
  // Initial fetch when component mounts (only if no initial data provided)
  useEffect(() => {
    if (!hasFetchedRef.current && !initialSharedAgents) {
      hasFetchedRef.current = true
      fetchSharedAgentsData()
    } else if (initialSharedAgents) {
      // If initial shared agents provided, mark as fetched and not loading
      hasFetchedRef.current = true
      setLoading(false)
    }
  }, [fetchSharedAgentsData, initialSharedAgents])
  
  // Handle refresh trigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > lastRefreshTriggerRef.current) {
      lastRefreshTriggerRef.current = refreshTrigger
      fetchSharedAgentsData(true) // Pass true for refresh
    }
  }, [refreshTrigger, fetchSharedAgentsData])
  
  // Update local state when initial shared agents change
  // But don't overwrite if we just removed an agent locally
  const justRemovedAgentRef = useRef<string | null>(null)
  const lastInitialSharedAgentsRef = useRef<SharedAgentResponse[] | undefined>(undefined)
  useEffect(() => {
    // Only update if the data has actually changed (not just a reference change)
    if (initialSharedAgents && 
        JSON.stringify(initialSharedAgents) !== JSON.stringify(lastInitialSharedAgentsRef.current)) {
      lastInitialSharedAgentsRef.current = initialSharedAgents
      
      // If we just removed an agent, filter it out from the initial list
      if (justRemovedAgentRef.current) {
        const filteredAgents = initialSharedAgents.filter(a => a.id !== justRemovedAgentRef.current)
        setLocalSharedAgents(filteredAgents)
        onSharedAgentsUpdate?.(filteredAgents)
        // Clear the flag after a short delay
        setTimeout(() => {
          justRemovedAgentRef.current = null
        }, 2000)
      } else {
        setLocalSharedAgents(initialSharedAgents)
        onSharedAgentsUpdate?.(initialSharedAgents)
      }
    }
  }, [initialSharedAgents, onSharedAgentsUpdate])

  const handleLeaveAgent = async () => {
    if (!agentToLeave?.id) return

    setLeaveLoading(true)
    try {
      await leaveSharedAgent(agentToLeave.id)
      toast.success(`Left "${agentToLeave.name}" successfully`)
      
      // Mark that we just removed this agent
      justRemovedAgentRef.current = agentToLeave.id
      
      // Remove the agent from local state immediately
      setLocalSharedAgents(prev => {
        const filtered = prev.filter(agent => agent.id !== agentToLeave.id)
        // Notify parent immediately with the updated count
        onAgentLeave?.(filtered.length)
        onSharedAgentsUpdate?.(filtered)
        return filtered
      })
      
      setShowLeaveModal(false)
      setAgentToLeave(null)
    } catch (err: unknown) {
      console.error('Failed to leave agent:', err)
      toast.error(err instanceof Error ? err.message : 'Failed to leave agent')
    } finally {
      setLeaveLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getPermissionBadge = (permission: string) => {
    switch (permission) {
      case 'write':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">
            <PencilIcon className="w-3 h-3 mr-1" />
            Write
          </span>
        )
      case 'read':
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
            <EyeIcon className="w-3 h-3 mr-1" />
            Read
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300 rounded-full">
            Unknown
          </span>
        )
    }
  }

  // Filter agents based on search term
  const filteredAgents = localSharedAgents.filter(agent =>
    agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.owner_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <ShareIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Shared Agents
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Agents shared with you by others
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''}
          </div>
          <button
            type="button"
            onClick={() => { void fetchSharedAgentsData() }}
            disabled={loading}
            className={clsx(
              'p-2 rounded-lg transition-colors',
              loading
                ? 'text-gray-400'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            )}
            title="Refresh shared agents"
          >
            <div className={clsx('w-5 h-5', loading && 'animate-spin')}>
              ⟳
            </div>
          </button>
        </div>
      </div>

      {/* Search */}
      {localSharedAgents.length > 0 && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search shared agents..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { void fetchSharedAgentsData() }}
              >
                Try Again
              </Button>
            </div>
          </div>
        ) : filteredAgents.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <ShareIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {searchTerm ? 'No shared agents match your search' : 'No shared agents'}
              </p>
              {!searchTerm && (
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Agents shared with you will appear here
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAgents.map((agent) => (
              <SharedAgentItem
                key={agent.id}
                agent={agent}
                onAgentSelect={onAgentSelect}
                onLeaveClick={(agent) => {
                  setAgentToLeave(agent)
                  setShowLeaveModal(true)
                }}
                formatDate={formatDate}
                getPermissionBadge={getPermissionBadge}
              />
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      {!loading && !error && localSharedAgents.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span>
                {localSharedAgents.filter(a => a.permission === 'read').length} read-only
              </span>
              <span>
                {localSharedAgents.filter(a => a.permission === 'write').length} read-write
              </span>
            </div>
            {searchTerm && (
              <span>
                {filteredAgents.length} of {localSharedAgents.length} shown
              </span>
            )}
          </div>
        </div>
      )}

      {/* Leave Agent Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLeaveModal && !leaveLoading}
        onClose={() => {
          if (!leaveLoading) {
            setShowLeaveModal(false)
            setAgentToLeave(null)
          }
        }}
        onConfirm={handleLeaveAgent}
        title="Leave Shared Agent"
        message={`Are you sure you want to leave "${agentToLeave?.name}"? You will lose access to this agent and will need to be re-invited to use it again.`}
        confirmText="Leave Agent"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}